import React, { type FC, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import type { SWRResponse } from "swr";
import useSWR from "swr";
import { useApiClient } from "@/hooks/useApiClient";
import { useIsMobile } from "@/hooks/useIsMobile";
import type { ZenrinMapRequestHistoryResponse } from "@/features/map/types";
import { ZenrinRequestHistoryFeedGrid } from "@/features/map/components/mapZenrinRequestHistory/ZenrinRequestHistoryFeedGrid";
import CircularProgress from "@mui/material/CircularProgress";
import type { GridSelectionModel } from "@mui/x-data-grid-pro";
import type { Map as LeafletMap } from "leaflet";

export interface ZenrinRequestHistoryProps {
  map: LeafletMap | null;
  open: boolean;
  onClose: () => void;
  onApplyToMap?: (
    row: ZenrinMapRequestHistoryResponse["results"][number]
  ) => void;
}

const ZENRIN_REQUEST_HISTORY_ENDPOINT = "/zenrin/image-request";
const ZENRIN_REQUEST_HISTORY_SUCCEEDED_STATUS = "succeeded";

/**
 * ZenrinRequestHistoryModal は、ゼンリンの地図リクエスト履歴を閲覧・表示するための
 * モーダルを表示する React の関数コンポーネントです。
 *
 * 地図生成リクエストの履歴を確認し、個別の記録を選択して、
 * 必要に応じて地図へ反映できます。履歴の取得には SWR を用いた
 * サーバーサイドデータを使用します。
 *
 * プロパティ:
 * - `map`: 選択したリクエストを反映できる地図インスタンス。
 * - `open`: モーダルが現在開いているかどうかを示す真偽値。
 * - `onClose`: モーダルを閉じる際に呼ばれるコールバック関数。
 * - `onApplyToMap`: 選択したリクエストを地図に反映する際に実行されるコールバック。
 *
 * 内部機能:
 * - モーダルが開いているときに SWR で履歴データを取得します。
 * - 選択した記録の状態に応じて「地図に反映する」ボタンの有効/無効を切り替えます。
 * - ローディング、エラー表示、非同期処理を扱います。
 */
export const ZenrinRequestHistoryModal: FC<ZenrinRequestHistoryProps> = ({
  map,
  open,
  onClose,
  onApplyToMap,
}) => {
  const isMobile = useIsMobile();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [selectedIds, setSelectedIds] = useState<GridSelectionModel>([]);

  const { apiClient } = useApiClient();

  const fetcher = async (
    url: string
  ): Promise<ZenrinMapRequestHistoryResponse> => {
    const response = await apiClient.get<ZenrinMapRequestHistoryResponse>(url);
    return response.data;
  };

  const swrResponse: SWRResponse<ZenrinMapRequestHistoryResponse, Error> =
    useSWR<ZenrinMapRequestHistoryResponse, Error>(
      open ? ZENRIN_REQUEST_HISTORY_ENDPOINT : null,
      fetcher
    );

  const { data, error, isLoading, isValidating, mutate } = swrResponse;

  // openがfalseからtrueへの切り替わり時のみ動作させるためのRef
  const prevOpenRef = useRef(false);
  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;

    if (!open) return;
    if (wasOpen) return;

    void mutate(); // モーダルを開くたびに必ず最新を取得
  }, [open, mutate]);

  const isError = Boolean(error);
  const isModalLoading = isLoading || isValidating;

  const rows = data?.results ?? [];

  const handleRowSelectionChange = (
    newSelectionModel: GridSelectionModel
  ): void => {
    if (newSelectionModel.length <= 1) {
      setSelectedIds(newSelectionModel);
      return;
    }
    setSelectedIds([newSelectionModel[newSelectionModel.length - 1]]);
  };

  const selectedRow = rows.find((row) => row.id === selectedIds[0]);
  const canApplyToMap =
    selectedRow?.status === ZENRIN_REQUEST_HISTORY_SUCCEEDED_STATUS;

  const handleApplyToMap = (): void => {
    if (!selectedRow || !canApplyToMap) return;
    onApplyToMap?.(selectedRow);
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (reason === "backdropClick") {
          return;
        }
        onClose();
      }}
      disableEscapeKeyDown
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        {/* SP は見出しとボタンが1行に収まらないので折り返す */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Typography variant="h6">地図取得履歴</Typography>
          <Box sx={{ display: "flex", gap: 1, whiteSpace: "nowrap" }}>
            <Button
              variant="contained"
              onClick={handleApplyToMap}
              disabled={!canApplyToMap}
            >
              地図に反映する
            </Button>
            <Button variant="outlined" onClick={onClose}>
              閉じる
            </Button>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            mt: 2,
            height: "70vh",
            width: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box pr={1} sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Typography variant="caption" color="text.secondary">
              ※ 下表の距離は現在表示している地図中心からの距離です
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflowY: "auto" }}>
            {isModalLoading ? (
              <Stack alignItems="center">
                <CircularProgress sx={{ my: "20px" }} />
              </Stack>
            ) : isError ? (
              <Stack alignItems="center">
                <Typography color="error" sx={{ my: "20px" }}>
                  データの取得に失敗しました。
                </Typography>
              </Stack>
            ) : (
              <ZenrinRequestHistoryFeedGrid
                rows={rows}
                map={map}
                selectedIds={selectedIds}
                onSelectionModelChange={handleRowSelectionChange}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
              />
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
